import type { PresenceUser } from '../types';

/**
 * Build a map of customerId → filtered presence users (excluding current user).
 * Includes Array.isArray guard to prevent crashes from malformed WebSocket data.
 *
 * Extracted from PipelineStageContent and OutcomesRow to eliminate duplication.
 */
export function buildCardPresenceMap(
  cards: Record<number, PresenceUser[]> | undefined,
  currentUserId: number | null
): Map<number, PresenceUser[]> {
  const map = new Map<number, PresenceUser[]>();
  if (!cards) return map;

  for (const key of Object.keys(cards)) {
    const customerId = Number(key);
    const entry = cards[customerId];
    if (!Array.isArray(entry)) continue;
    const filtered = entry.filter((p) => p.userId !== currentUserId);
    if (filtered.length > 0) {
      map.set(customerId, filtered);
    }
  }
  return map;
}

/**
 * Create a stable callback for registering customer card DOM refs.
 * Returns a function suitable for use in useMemo/useCallback.
 *
 * Extracted from PipelineStageContent and OutcomesRow to eliminate duplication.
 */
export function makeRegisterCardRef(
  cardRefs: React.MutableRefObject<Map<number, HTMLDivElement>>
): (customerId: number, node: HTMLDivElement | null) => void {
  return (customerId: number, node: HTMLDivElement | null) => {
    if (node) {
      cardRefs.current.set(customerId, node);
    } else {
      cardRefs.current.delete(customerId);
    }
  };
}
